import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateTime, downloadCsv, humanize, money, toIsoDate, todayIso, type CsvColumn } from '@/lib/utils';
import { DataTable, FilterBar, FilterField, PageHeader, SearchFilterBar, Tabs, type DataTableColumn } from '@/components/ui/layout';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, StatusBadge } from '@/components/ui/primitives';
import { CriticalHistoryTab, UnbilledTab } from './LabReportsExtra';
import { moneyDigits } from '@/components/MoneyInput';

const PAGE = 50;
const MAX_DAYS = 366;
const DAY_MS = 86_400_000;

interface Range {
  from: string;
  to: string;
}

interface VolumeRow {
  testId: string | null;
  code: string | null;
  name: string;
  department: string | null;
  tests: number;
  revenueMinor: number;
}

interface VolumeReport {
  groupBy: 'test' | 'department';
  data: VolumeRow[];
  nextOffset: number | null;
}

interface ReferrerRow {
  referrer: string;
  orders: number;
  tests: number;
  revenueMinor: number;
}

interface ReferrerReport {
  data: ReferrerRow[];
  nextOffset: number | null;
}

interface TurnaroundRow {
  testId: string;
  code: string;
  name: string;
  released: number;
  averageMinutes: number;
  p90Minutes: number;
  breachedTat: number;
}

interface TurnaroundReport {
  data: TurnaroundRow[];
  nextOffset: number | null;
}

interface RejectionRow {
  testId: string;
  code: string;
  name: string;
  collected: number;
  rejected: number;
  reruns: number;
  rejectionRatePct: number;
}

interface RejectionReport {
  data: RejectionRow[];
  reasons: { reason: string; count: number }[];
  nextOffset: number | null;
}

interface CancelledRefundRow {
  orderId: string;
  orderNumber: string;
  patientName: string;
  mrn: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceStatus: string;
  paidMinor: number;
  invoiceNetMinor: number;
  cancelledNetMinor: number;
  refundDueMinor: number;
  cancelledTests: number;
  cancelledAt: string | null;
  reason: string | null;
}

interface CancelledRefundPage {
  data: CancelledRefundRow[];
  nextCursor: string | null;
}

type ReportColumn<T> = DataTableColumn<T>;

export interface ReportFilterProps {
  rangeFields: ReactNode;
  problem: string | null;
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
}

function rangeProblem(range: Range): string | null {
  if (!range.from || !range.to) return 'Pick both a start and an end date.';
  const days = daysBetween(range.from, range.to);
  if (Number.isNaN(days)) return 'Pick both a start and an end date.';
  if (days < 1) return 'The end date must not be before the start date.';
  if (days > MAX_DAYS) return `A report covers at most ${MAX_DAYS} days. Shorten the range.`;
  return null;
}

function minutesText(value: number): string {
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours < 24) return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days} d ${rest} h` : `${days} d`;
}

function majorUnits(minor: number): string {
  return (minor / 100).toFixed(moneyDigits());
}

function percent(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : '0%';
}

function useOffsetReport<R extends { nextOffset: number | null }>(
  name: string,
  range: Range,
  extra: Record<string, string>,
  enabled: boolean,
  fetcher: (query: string) => Promise<R>,
) {
  const scope = `${name}|${range.from}|${range.to}|${JSON.stringify(extra)}`;
  const [paging, setPaging] = useState({ scope, offset: 0 });
  const offset = paging.scope === scope ? paging.offset : 0;

  const query = useQuery({
    queryKey: ['lab-report', name, range.from, range.to, extra, offset],
    queryFn: () => fetcher(qs({ from: range.from, to: range.to, ...extra, limit: PAGE, offset })),
    placeholderData: keepPreviousData,
    enabled,
  });

  return { query, offset, goTo: (value: number) => setPaging({ scope, offset: value }) };
}

function ReportView<T>({
  rows,
  loading,
  fetching,
  error,
  empty,
  columns,
  rowKey,
  offset,
  nextOffset,
  onPage,
  csvName,
  csvColumns,
  range,
  before,
  toolbar,
  rangeFields,
  problem,
}: {
  rows: T[] | undefined;
  loading: boolean;
  fetching: boolean;
  error: unknown;
  empty: string;
  columns: ReportColumn<T>[];
  rowKey: (row: T) => string;
  offset: number;
  nextOffset: number | null | undefined;
  onPage: (offset: number) => void;
  csvName: string;
  csvColumns: CsvColumn<T>[];
  range: Range;
  before?: ReactNode;
  toolbar?: ReactNode;
  rangeFields: ReactNode;
  problem: string | null;
}) {
  const shown = rows?.length ?? 0;
  const paged = (offset > 0 || nextOffset !== null) && nextOffset !== undefined;
  return (
    <>
      <SearchFilterBar
        filters={
          <>
            {rangeFields}
            {toolbar}
          </>
        }
        trailing={
          <Button
            variant="outline"
            size="sm"
            disabled={shown === 0}
            onClick={() => downloadCsv(`${csvName}-${range.from}-to-${range.to}`, rows ?? [], csvColumns)}
          >
            <Download className="h-3.5 w-3.5" /> {tr('lab', 'export_this_page_csv')}
          </Button>
        }
      />
      {problem ? (
        <Alert variant="warning">{problem}</Alert>
      ) : (
        <>
          {before}
          <div className={fetching && !loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <DataTable
              rows={rows}
              loading={loading}
              error={error}
              rowKey={rowKey}
              empty={empty}
              columns={columns}
              footer={
                paged ? (
                  <div className="flex w-full items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">{shown > 0 ? tr('lab', 'rows_x_to_x', { value: offset + 1, value2: offset + shown }) : ''}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={offset === 0 || fetching} onClick={() => onPage(Math.max(0, offset - PAGE))}>
                        {tr('lab', 'previous_page')}
                      </Button>
                      <Button variant="outline" size="sm" disabled={nextOffset === null || fetching} onClick={() => onPage(nextOffset ?? offset)}>
                        {tr('lab', 'next_page')}
                      </Button>
                    </div>
                  </div>
                ) : undefined
              }
            />
          </div>
        </>
      )}
    </>
  );
}

export function LabReportsPage() {
  const { can, hasModule } = useAuth();
  const showFinancial = can('report.financial', 'lab.result.verify', 'lab.master.manage');
  const showCriticals = can('lab.result.verify', 'lab.critical.acknowledge', 'lab.master.manage', 'radiology.report.verify', 'radiology.critical.acknowledge', 'report.read.all');
  const showUnbilled = can('report.financial', 'lab.master.manage');
  const [tab, setTab] = useState(showFinancial ? 'volume' : 'turnaround');
  const [range, setRange] = useState<Range>(() => ({
    from: toIsoDate(new Date(Date.now() - 29 * DAY_MS)),
    to: todayIso(),
  }));
  const showRefunds = can('report.financial', 'payment.refund', 'payment.refund.approve', 'lab.master.manage');
  const problem = rangeProblem(range);
  const rangeFields = (
    <>
      <FilterField label={tr('lab', 'from')}>
        <DatePicker
          className="w-44"
          value={range.from}
          max={range.to || undefined}
          onChange={(value) => setRange((current) => ({ ...current, from: value }))}
        />
      </FilterField>
      <FilterField label={tr('lab', 'to')}>
        <DatePicker
          className="w-44"
          value={range.to}
          min={range.from || undefined}
          onChange={(value) => setRange((current) => ({ ...current, to: value }))}
        />
      </FilterField>
      <p className="self-center text-xs text-muted-foreground">{tr('lab', 'up_to')} {MAX_DAYS} {tr('lab', 'days')}</p>
    </>
  );
  const filterProps: ReportFilterProps = { rangeFields, problem };

  return (
    <>
      <PageHeader
        title={tr('lab', 'lab_reports')}
        description={tr('lab', 'what_the_laboratory_did_for_whom_how_fast_and')}
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          ...(showFinancial ? [{ id: 'volume', label: tr('lab', 'volume_and_revenue') }, { id: 'referrers', label: tr('lab', 'referring_doctors') }] : []),
          { id: 'turnaround', label: tr('lab', 'turnaround_by_test') },
          { id: 'rejections', label: tr('lab', 'rejections') },
          ...(showCriticals ? [{ id: 'criticals', label: tr('lab', 'critical_values') }] : []),
          ...(showUnbilled ? [{ id: 'unbilled', label: tr('lab', 'orders_without_a_bill') }] : []),
          ...(showRefunds ? [{ id: 'refunds', label: tr('lab', 'cancelled_with_money_to_refund') }] : []),
        ]}
      />

      {tab === 'volume' && showFinancial && <VolumeTab range={range} {...filterProps} />}
      {tab === 'referrers' && showFinancial && <ReferrersTab range={range} {...filterProps} />}
      {tab === 'criticals' && showCriticals && <CriticalHistoryTab range={range} showRadiology={hasModule('radiology')} {...filterProps} />}
      {tab === 'unbilled' && showUnbilled && (
        <>
          <FilterBar>{rangeFields}</FilterBar>
          {problem ? (
            <Alert variant="warning">{problem}</Alert>
          ) : (
            <>
              <UnbilledTab range={range} radiology={false} />
              {hasModule('radiology') && can('report.financial', 'radiology.master.manage') && (
                <>
                  <h3 className="mb-2 mt-6 text-sm font-semibold">{tr('lab', 'imaging')}</h3>
                  <UnbilledTab range={range} radiology />
                </>
              )}
            </>
          )}
        </>
      )}
      {tab === 'turnaround' && <TurnaroundTab range={range} {...filterProps} />}
      {tab === 'rejections' && <RejectionsTab range={range} {...filterProps} />}
      {tab === 'refunds' && showRefunds && <CancelledRefundsTab />}
    </>
  );
}

function VolumeTab({ range, rangeFields, problem }: { range: Range } & ReportFilterProps) {
  const [groupBy, setGroupBy] = useState<'test' | 'department'>('test');
  const { query, offset, goTo } = useOffsetReport<VolumeReport>('volume', range, { groupBy }, problem === null, (search) =>
    get<VolumeReport>(`/lab/reports/volume${search}`),
  );
  const shownBy = query.data?.groupBy ?? groupBy;

  const columns: ReportColumn<VolumeRow>[] =
    shownBy === 'test'
      ? [
          { header: tr('lab', 'code'), width: '110px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code ?? '—'}</span> },
          { header: tr('lab', 'test'), sort: (row) => row.name, cell: (row) => row.name },
          { header: tr('lab', 'department'), sort: (row) => row.department, cell: (row) => humanize(row.department) },
          { header: tr('lab', 'tests'), align: 'right', sort: (row) => row.tests, cell: (row) => row.tests },
          { header: tr('lab', 'revenue'), align: 'right', sort: (row) => row.revenueMinor, cell: (row) => money(row.revenueMinor) },
        ]
      : [
          { header: tr('lab', 'department'), sort: (row) => row.name, cell: (row) => humanize(row.name) },
          { header: tr('lab', 'tests'), align: 'right', sort: (row) => row.tests, cell: (row) => row.tests },
          { header: tr('lab', 'revenue'), align: 'right', sort: (row) => row.revenueMinor, cell: (row) => money(row.revenueMinor) },
        ];

  const csvColumns: CsvColumn<VolumeRow>[] =
    shownBy === 'test'
      ? [
          { header: tr('lab', 'code'), value: (row) => row.code },
          { header: tr('lab', 'test'), value: (row) => row.name },
          { header: tr('lab', 'department'), value: (row) => row.department },
          { header: tr('lab', 'tests'), value: (row) => row.tests },
          { header: tr('lab', 'revenue'), value: (row) => majorUnits(row.revenueMinor) },
        ]
      : [
          { header: tr('lab', 'department'), value: (row) => row.name },
          { header: tr('lab', 'tests'), value: (row) => row.tests },
          { header: tr('lab', 'revenue'), value: (row) => majorUnits(row.revenueMinor) },
        ];

  return (
    <ReportView
      rows={query.data?.data}
      loading={query.isLoading}
      fetching={query.isFetching}
      error={query.error}
      empty={tr('lab', 'no_tests_were_ordered_in_this_period')}
      columns={columns}
      rowKey={(row) => `${row.testId ?? ''}|${row.name}`}
      offset={offset}
      nextOffset={query.data?.nextOffset}
      onPage={goTo}
      csvName={`lab-volume-by-${shownBy}`}
      csvColumns={csvColumns}
      range={range}
      rangeFields={rangeFields}
      problem={problem}
      toolbar={
        <FilterField label={tr('lab', 'group_by')}>
          <div className="flex gap-1" role="group" aria-label={tr('lab', 'group_by')}>
            <Button size="sm" variant={groupBy === 'test' ? 'default' : 'outline'} aria-pressed={groupBy === 'test'} onClick={() => setGroupBy('test')}>
              {tr('lab', 'by_test')}
            </Button>
            <Button size="sm" variant={groupBy === 'department' ? 'default' : 'outline'} aria-pressed={groupBy === 'department'} onClick={() => setGroupBy('department')}>
              {tr('lab', 'by_department')}
            </Button>
          </div>
        </FilterField>
      }
    />
  );
}

function ReferrersTab({ range, rangeFields, problem }: { range: Range } & ReportFilterProps) {
  const { query, offset, goTo } = useOffsetReport<ReferrerReport>('referrers', range, {}, problem === null, (search) =>
    get<ReferrerReport>(`/lab/reports/referrers${search}`),
  );
  const label = (row: ReferrerRow) => (row.referrer === '(none)' ? 'No referring doctor recorded' : row.referrer);

  return (
    <ReportView
      rows={query.data?.data}
      loading={query.isLoading}
      fetching={query.isFetching}
      error={query.error}
      empty={tr('lab', 'no_orders_in_this_period')}
      columns={[
        { header: tr('lab', 'referring_doctor'), sort: (row) => label(row), cell: (row) => label(row) },
        { header: tr('lab', 'orders'), align: 'right', sort: (row) => row.orders, cell: (row) => row.orders },
        { header: tr('lab', 'tests'), align: 'right', sort: (row) => row.tests, cell: (row) => row.tests },
        { header: tr('lab', 'revenue'), align: 'right', sort: (row) => row.revenueMinor, cell: (row) => money(row.revenueMinor) },
      ]}
      rowKey={(row) => row.referrer}
      offset={offset}
      nextOffset={query.data?.nextOffset}
      onPage={goTo}
      csvName="lab-referring-doctors"
      csvColumns={[
        { header: tr('lab', 'referring_doctor'), value: label },
        { header: tr('lab', 'orders'), value: (row) => row.orders },
        { header: tr('lab', 'tests'), value: (row) => row.tests },
        { header: tr('lab', 'revenue'), value: (row) => majorUnits(row.revenueMinor) },
      ]}
      range={range}
      rangeFields={rangeFields}
      problem={problem}
    />
  );
}

function TurnaroundTab({ range, rangeFields, problem }: { range: Range } & ReportFilterProps) {
  const { query, offset, goTo } = useOffsetReport<TurnaroundReport>('turnaround', range, {}, problem === null, (search) =>
    get<TurnaroundReport>(`/lab/reports/turnaround-by-test${search}`),
  );

  return (
    <ReportView
      rows={query.data?.data}
      loading={query.isLoading}
      fetching={query.isFetching}
      error={query.error}
      empty={tr('lab', 'no_reports_were_released_in_this_period')}
      columns={[
        { header: tr('lab', 'code'), width: '110px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
        { header: tr('lab', 'test'), sort: (row) => row.name, cell: (row) => row.name },
        { header: tr('lab', 'released'), align: 'right', sort: (row) => row.released, cell: (row) => row.released },
        { header: tr('lab', 'average'), align: 'right', sort: (row) => row.averageMinutes, cell: (row) => minutesText(row.averageMinutes) },
        { header: tr('lab', '90th_percentile'), align: 'right', sort: (row) => row.p90Minutes, cell: (row) => minutesText(row.p90Minutes) },
        {
          header: tr('lab', 'beyond_target'),
          align: 'right',
          sort: (row) => row.breachedTat,
          cell: (row) => (
            <span className={row.breachedTat > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'}>
              {row.breachedTat} ({percent(row.breachedTat, row.released)})
            </span>
          ),
        },
      ]}
      rowKey={(row) => row.testId}
      offset={offset}
      nextOffset={query.data?.nextOffset}
      onPage={goTo}
      csvName="lab-turnaround-by-test"
      csvColumns={[
        { header: tr('lab', 'code'), value: (row) => row.code },
        { header: tr('lab', 'test'), value: (row) => row.name },
        { header: tr('lab', 'released'), value: (row) => row.released },
        { header: tr('lab', 'average_minutes'), value: (row) => row.averageMinutes },
        { header: tr('lab', '90th_percentile_minutes'), value: (row) => row.p90Minutes },
        { header: tr('lab', 'beyond_target'), value: (row) => row.breachedTat },
      ]}
      range={range}
      rangeFields={rangeFields}
      problem={problem}
      before={
        <p className="mb-3 text-xs text-muted-foreground">
          {tr('lab', 'average_and_90th_percentile_run_from_the_order')}
        </p>
      }
    />
  );
}

function RejectionsTab({ range, rangeFields, problem }: { range: Range } & ReportFilterProps) {
  const { query, offset, goTo } = useOffsetReport<RejectionReport>('rejections', range, {}, problem === null, (search) =>
    get<RejectionReport>(`/lab/reports/rejections${search}`),
  );
  const reasons = query.data?.reasons ?? [];

  return (
    <ReportView
      rows={query.data?.data}
      loading={query.isLoading}
      fetching={query.isFetching}
      error={query.error}
      empty={tr('lab', 'no_specimens_were_collected_or_rejected_in_this')}
      columns={[
        { header: tr('lab', 'code'), width: '110px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
        { header: tr('lab', 'test'), sort: (row) => row.name, cell: (row) => row.name },
        { header: tr('lab', 'collected'), align: 'right', sort: (row) => row.collected, cell: (row) => row.collected },
        { header: tr('lab', 'rejected'), align: 'right', sort: (row) => row.rejected, cell: (row) => row.rejected },
        { header: tr('lab', 'reruns'), align: 'right', sort: (row) => row.reruns, cell: (row) => row.reruns },
        {
          header: tr('lab', 'rejection_rate'),
          align: 'right',
          sort: (row) => row.rejectionRatePct,
          cell: (row) => (
            <span className={row.rejected > 0 ? 'font-semibold' : 'text-muted-foreground'}>{row.rejectionRatePct}%</span>
          ),
        },
      ]}
      rowKey={(row) => row.testId}
      offset={offset}
      nextOffset={query.data?.nextOffset}
      onPage={goTo}
      csvName="lab-rejections"
      csvColumns={[
        { header: tr('lab', 'code'), value: (row) => row.code },
        { header: tr('lab', 'test'), value: (row) => row.name },
        { header: tr('lab', 'collected'), value: (row) => row.collected },
        { header: tr('lab', 'rejected'), value: (row) => row.rejected },
        { header: tr('lab', 'reruns'), value: (row) => row.reruns },
        { header: tr('lab', 'rejection_rate_2'), value: (row) => row.rejectionRatePct },
      ]}
      range={range}
      rangeFields={rangeFields}
      problem={problem}
      before={
        reasons.length > 0 ? (
          <Card className="mb-3">
            <CardHeader>
              <CardTitle className="text-sm">{tr('lab', 'why_specimens_were_rejected')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {reasons.map((entry) => (
                  <li key={entry.reason} className="flex items-center justify-between gap-4">
                    <span>{entry.reason}</span>
                    <span className="tabular text-muted-foreground">{entry.count}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : undefined
      }
    />
  );
}

function CancelledRefundsTab() {
  const { can, hasModule } = useAuth();
  const linkable = hasModule('billing') && can('invoice.read', 'invoice.create');

  const query = useInfiniteQuery({
    queryKey: ['lab-cancelled-refunds'],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<CancelledRefundPage>(`/lab/orders/cancelled-refunds${qs({ limit: PAGE, cursor: pageParam, dueOnly: true })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = query.data?.pages.flatMap((page) => page.data);

  const stillBilled = (row: CancelledRefundRow) => row.invoiceNetMinor - row.cancelledNetMinor;

  return (
    <>
      <FilterBar
        trailing={
          <Button
            variant="outline"
            size="sm"
            disabled={!rows || rows.length === 0}
            onClick={() =>
              downloadCsv('lab-cancelled-with-money-to-refund', rows ?? [], [
                { header: tr('lab', 'order'), value: (row) => row.orderNumber },
                { header: tr('lab', 'patient'), value: (row) => row.patientName },
                { header: tr('lab', 'mrn'), value: (row) => row.mrn },
                { header: tr('lab', 'invoice'), value: (row) => row.invoiceNumber },
                { header: tr('lab', 'invoice_status'), value: (row) => row.invoiceStatus },
                { header: tr('lab', 'paid'), value: (row) => majorUnits(row.paidMinor) },
                { header: tr('lab', 'still_billed'), value: (row) => majorUnits(stillBilled(row)) },
                { header: tr('lab', 'refund_due'), value: (row) => majorUnits(row.refundDueMinor) },
                { header: tr('lab', 'cancelled_tests'), value: (row) => row.cancelledTests },
                { header: tr('lab', 'cancelled_at'), value: (row) => row.cancelledAt },
                { header: tr('lab', 'reason'), value: (row) => row.reason },
              ])
            }
          >
            <Download className="h-3.5 w-3.5" /> {tr('lab', 'export_loaded_rows_csv')}
          </Button>
        }
      >
        <p className="self-center text-sm text-muted-foreground">
          {tr('lab', 'tests_on_these_orders_were_cancelled_after_the')}
          {linkable ? ' Open the invoice to see its payments.' : ''}
        </p>
      </FilterBar>

      <DataTable
        rows={rows}
        loading={query.isLoading} refreshing={query.isFetching}
        error={query.error}
        rowKey={(row) => `${row.orderId}:${row.invoiceId}`}
        empty={tr('lab', 'no_cancelled_order_is_holding_money_that_needs')}
        footer={
          query.hasNextPage ? (
            <div className="flex w-full justify-center">
              <Button variant="outline" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                {tr('lab', 'load_more')}
              </Button>
            </div>
          ) : undefined
        }
        columns={[
          {
            header: tr('lab', 'order'),
            sort: (row) => row.orderNumber,
            cell: (row) => (
              <div>
                <p className="font-mono text-xs">{row.orderNumber}</p>
                <p className="text-xs text-muted-foreground">
                  {row.cancelledTests} {tr('lab', 'test_2')}{row.cancelledTests === 1 ? '' : 's'} {tr('lab', 'cancelled')} {dateTime(row.cancelledAt)}
                </p>
              </div>
            ),
          },
          {
            header: tr('lab', 'patient'),
            sort: (row) => row.patientName,
            cell: (row) => (
              <div>
                <p>{row.patientName}</p>
                <p className="text-xs text-muted-foreground">{row.mrn}</p>
              </div>
            ),
          },
          {
            header: tr('lab', 'invoice'),
            sort: (row) => row.invoiceNumber,
            cell: (row) => (
              <div className="space-y-1">
                {linkable ? (
                  <Link className="underline" to={`/billing/invoices/${row.invoiceId}`}>
                    {row.invoiceNumber}
                  </Link>
                ) : (
                  <span>{row.invoiceNumber}</span>
                )}
                <div>
                  <StatusBadge status={row.invoiceStatus} />
                </div>
              </div>
            ),
          },
          { header: tr('lab', 'paid'), align: 'right', sort: (row) => row.paidMinor, cell: (row) => money(row.paidMinor) },
          { header: tr('lab', 'still_billed'), align: 'right', sort: (row) => stillBilled(row), cell: (row) => money(stillBilled(row)) },
          {
            header: tr('lab', 'refund_due'),
            align: 'right',
            sort: (row) => row.refundDueMinor,
            cell: (row) => (
              <span className={row.refundDueMinor > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'}>
                {money(row.refundDueMinor)}
              </span>
            ),
          },
          { header: tr('lab', 'reason'), sort: (row) => row.reason, cell: (row) => <span className="text-xs text-muted-foreground">{row.reason ?? '—'}</span> },
        ]}
      />
    </>
  );
}
